import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaWhatsapp, 
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import './WhatsappFormulaire.css';

const WhatsappFormulaire = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    treatment: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const treatments = [
    'Consultation générale',
    'Détartrage et nettoyage',
    'Blanchiment dentaire',
    'Implant dentaire',
    'Traitement de canal',
    'Orthodontie',
    'Pose de couronne',
    'Extraction dentaire',
    'Urgence dentaire',
    'Autre'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    }

    const phoneRegex = /^(?:(?:\+|00)212|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (!formData.date) {
      newErrors.date = 'La date est requise';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.date = 'La date doit être future';
      }
    }

    if (!formData.time) {
      newErrors.time = "L'heure est requise";
    }

    if (!formData.treatment) {
      newErrors.treatment = 'Le traitement est requis';
    }

    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Fonction pour détecter iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Fonction pour ouvrir WhatsApp (compatible iOS et Android)
  const openWhatsApp = (url) => {
    if (isIOS()) {
      // Sur iOS, plusieurs méthodes pour assurer la compatibilité
      // Méthode 1: Créer un lien et le cliquer (fonctionne dans le contexte d'un événement utilisateur)
      try {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.position = 'fixed';
        link.style.top = '-1000px';
        link.style.left = '-1000px';
        document.body.appendChild(link);
        
        // Déclencher le clic
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(clickEvent);
        
        // Nettoyer après un court délai
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 1000);
      } catch (error) {
        console.log('Méthode 1 échouée, tentative avec window.location:', error);
        // Méthode 2: Fallback avec window.location.href
        window.location.href = url;
      }
    } else {
      // Sur Android et autres, utiliser window.open
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const clinicPhone = "212667271927";
    const message = `🦷 *Demande de Rendez-vous - Clinique Dentaire*\n
👤 *Nom:* ${formData.name}
📞 *Téléphone:* ${formData.phone}
📅 *Date souhaitée:* ${formData.date}
⏰ *Heure souhaitée:* ${formData.time}
🦷 *Type de traitement:* ${formData.treatment}\n
*Cette demande a été envoyée via le site web.*`;

    const url = `https://wa.me/${clinicPhone}?text=${encodeURIComponent(message)}`;
    
    setIsSubmitting(true);
    setShowSuccess(true);

    // Sur iOS, ouvrir immédiatement dans le contexte de l'événement utilisateur
    // pour éviter les blocages de popup
    if (isIOS()) {
      // Ouvrir immédiatement pour iOS
      openWhatsApp(url);
      
      // Afficher le message de succès brièvement puis réinitialiser
      setTimeout(() => {
        setIsSubmitting(false);
        setShowSuccess(false);
        resetForm();
      }, 1500);
    } else {
      // Pour Android, animation puis ouverture
      setTimeout(() => {
        setIsSubmitting(false);
        openWhatsApp(url);
        
        setTimeout(() => {
          setShowSuccess(false);
          resetForm();
        }, 2000);
      }, 1500);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      date: '',
      time: '',
      treatment: ''
    });
    setErrors({});
  };

  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <motion.div 
      className="whatsapp-formulaire-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="form-header">
        <div className="header-content">
          <div className="header-icon">
            <FaWhatsapp />
          </div>
          <div className="header-text">
            <h3 className="form-title-white">Prise de Rendez-vous</h3>
            <p className="form-subtitle">Remplissez le formulaire et nous vous contacterons via WhatsApp</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            className="success-message"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FaCheckCircle className="success-icon" />
            <div>
              <h4>Demande envoyée !</h4>
              <p>Redirection vers WhatsApp...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form className="whatsapp-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            Nom complet
          </label>
          <input
            id="name"
            type="text"
            placeholder="Votre nom et prénom"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={errors.name ? 'error' : ''}
          />
          {errors.name && (
            <motion.span 
              className="error-message"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FaExclamationCircle /> {errors.name}
            </motion.span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">
            Numéro de téléphone
          </label>
          <input
            id="phone"
            type="tel"
            placeholder="Ex: +212 6 XX XX XX XX"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className={errors.phone ? 'error' : ''}
          />
          {errors.phone && (
            <motion.span className="error-message">
              <FaExclamationCircle /> {errors.phone}
            </motion.span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">
              Date souhaitée
            </label>
            <input
              id="date"
              type="date"
              value={formData.date}
              min={getCurrentDate()}
              onChange={(e) => handleChange('date', e.target.value)}
              className={errors.date ? 'error' : ''}
            />
            {errors.date && (
              <motion.span className="error-message">
                <FaExclamationCircle /> {errors.date}
              </motion.span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="time">
              Heure souhaitée
            </label>
            <input
              id="time"
              type="time"
              value={formData.time}
              onChange={(e) => handleChange('time', e.target.value)}
              className={errors.time ? 'error' : ''}
            />
            {errors.time && (
              <motion.span className="error-message">
                <FaExclamationCircle /> {errors.time}
              </motion.span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="treatment">
            Type de traitement
          </label>
          <select
            id="treatment"
            value={formData.treatment}
            onChange={(e) => handleChange('treatment', e.target.value)}
            className={errors.treatment ? 'error' : ''}
          >
            <option value="">Sélectionnez un traitement</option>
            {treatments.map((treatment, index) => (
              <option key={index} value={treatment}>
                {treatment}
              </option>
            ))}
          </select>
          {errors.treatment && (
            <motion.span className="error-message">
              <FaExclamationCircle /> {errors.treatment}
            </motion.span>
          )}
        </div>
        <motion.button
          type="submit"
          className="whatsapp-button"
          disabled={isSubmitting}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isSubmitting ? (
            <>
              <div className="spinner"></div>
              Envoi en cours...
            </>
          ) : (
            <>
              <FaWhatsapp className="whatsapp-icon" />
              Envoyer sur WhatsApp
            </>
          )}
        </motion.button>

        <div className="form-footer">
          <p className="privacy-notice">
            <FaExclamationCircle />
            Vos informations sont uniquement utilisées pour votre rendez-vous.
          </p>
        </div>
      </form>
    </motion.div>
  );
};

export default WhatsappFormulaire;